/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React from "react";
import { useLiveRequests, requestStatusTone as statusTone, requestAgo as ago } from "./useLiveRequests";
import { clientLabel } from "@/api/requests";

export const LiveRequestsWidget: React.FC = () => {
  const [showProbes, setShowProbes] = React.useState(false);
  const { enabled, rows, isError, baseline } = useLiveRequests();
  const visible = showProbes ? rows : rows.filter((r) => !r.probe);
  const probeCount = rows.length - rows.filter((r) => !r.probe).length;

  return (
    <section className="gh-w cc-live">
      <div className="gh-w-head">
        <span className="gh-label">
          <span className="mr-1.5 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500 align-middle" />
          Live requests
        </span>
        {enabled && probeCount > 0 && (
          <button
            type="button"
            onClick={() => setShowProbes((v) => !v)}
            className="gh-sub cursor-pointer hover:text-gray-700 dark:hover:text-gray-300"
            title="Nyrvex's own health checks"
          >
            {showProbes ? "hide probes" : `+${probeCount} probes`}
          </button>
        )}
      </div>

      {!enabled ? (
        <div className="gh-empty">
          not configured — set <code>[request_log]</code> in config
        </div>
      ) : visible.length === 0 ? (
        <div className="gh-empty">{isError ? "unavailable" : "waiting for traffic…"}</div>
      ) : (
        <div className="flex flex-1 min-h-0 flex-col gap-px cc-scroll overflow-y-auto font-mono text-[11px] leading-tight">
          {visible.map((r) => (
            <div
              key={r.id}
              className={`flex items-center gap-2 py-0.5${r.id > baseline ? " req-in" : ""}`}
              title={`${r.method} ${r.host}${r.path} · ${r.status} · ${r.clientHost ? `${r.clientHost} (${r.clientIp})` : r.clientIp} · ${(+r.durationMs).toFixed(0)}ms${
                r.userAgent ? ` · ${r.userAgent}` : ""
              }`}
            >
              <span
                className={`flex-none rounded-[3px] border px-1 tabular-nums ${statusTone(r.status)}`}
              >
                {r.status}
              </span>
              <span className="flex-none text-gray-400 dark:text-gray-500">{r.method}</span>
              <span className="flex-none max-w-[32%] truncate font-sans font-medium text-gray-700 dark:text-gray-300">
                {r.service || r.host}
              </span>
              <span className="min-w-0 flex-1 truncate text-gray-500 dark:text-gray-400">{r.path}</span>
              <span className="max-w-[26%] flex-none truncate text-gray-400 dark:text-gray-500">{clientLabel(r)}</span>
              <span className="flex-none tabular-nums text-gray-400 dark:text-gray-500">
                {+r.durationMs < 1 ? "<1" : (+r.durationMs).toFixed(0)}ms
              </span>
              <span className="flex-none tabular-nums text-gray-300 dark:text-gray-600">{ago(r.time)}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
